import { useCallback, useEffect, useState } from 'react';
import { observer } from 'mobx-react';
import { Link } from 'react-router-dom';
import { Alert, Button, Input, Modal, Segmented, Skeleton, Space, Typography, message } from 'antd';
import { CheckCircleFilled, ExclamationCircleFilled, InfoCircleFilled, ReloadOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import {
  decideScenario,
  getAgentEvaluationOverview,
  type AgentEvaluationOverview,
  type EvaluationHeadline,
  type FindingDisposition,
  type ScenarioApprovalDecision,
  type ScenarioStanding,
  type ScenarioStandingRow,
} from '@/api/validationRuns';
import { SectionCard } from '@/components/SectionCard';
import { StatusPill, type PillTone } from '@/components/StatusPill';
import { useStore } from '@/stores';
import { colors } from '@/theme/tokens';
import { FindingDrawer } from './FindingDrawer';
import { RunRequestsPanel } from './RunRequestsPanel';
import { ReleasePanel } from './ReleasePanel';

dayjs.extend(relativeTime);

const { Text } = Typography;

// ─────────────────────────────────────────────────────────────────────────────
// AgentEvaluationTab — EVAL-06 STUDIO-EVAL-05 (workspace plan §10.1). Answers the owner's question first: is this
// agent, AS IT IS NOW, doing its job? The headline, its reasons, what changed since the last run, then every active
// scenario's standing on the current version — failures first, safety first. Every judgement is the server's; a
// count opens its evidence. It never says "safe": "Eligible for release review" is a review, not an approval.
// ─────────────────────────────────────────────────────────────────────────────

const HEADLINE: Record<EvaluationHeadline, { title: string; tone: PillTone; icon: React.ReactNode }> = {
  NeedsAttention: { title: 'Needs attention', tone: 'red', icon: <ExclamationCircleFilled /> },
  EvidenceIncomplete: { title: 'Evidence incomplete', tone: 'amber', icon: <InfoCircleFilled /> },
  EligibleForReleaseReview: { title: 'Eligible for release review', tone: 'green', icon: <CheckCircleFilled /> },
};

const FINDING: Record<FindingDisposition, { label: string; tone: PillTone }> = {
  Open: { label: 'Nobody on it yet', tone: 'red' },
  Acknowledged: { label: 'Being looked at', tone: 'amber' },
  Resolved: { label: 'Resolved', tone: 'green' },
  Disputed: { label: 'Disputed', tone: 'violet' },
};

/**
 * A reviewer's decision on what one scenario's CURRENT version expects (clinical approval evidence for the release
 * checklist). Two fields and a choice, so a small dialog. The server refuses it if the scenario changed meanwhile.
 */
function reviewExpectations(agentId: number, row: ScenarioStandingRow, onDone: () => void) {
  let decision: ScenarioApprovalDecision = 'Approved';
  let role = '';
  let justification = '';
  Modal.confirm({
    title: `Review what ${row.scenarioId} expects`,
    icon: null,
    width: 560,
    okText: 'Record',
    cancelText: 'Cancel',
    content: (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10, paddingTop: 8 }}>
        <Text type="secondary" style={{ fontSize: 12 }}>
          {`You are deciding on version ${row.currentSnapshotId ?? '?'} of this scenario. Read it in the Scenario library first; an edit makes a new version that needs its own review.`}
        </Text>
        <Segmented
          defaultValue="Approved"
          onChange={(v) => { decision = v as ScenarioApprovalDecision; }}
          options={[
            { value: 'Approved', label: 'Approve' },
            { value: 'ChangesRequested', label: 'Request changes' },
            { value: 'Withdrawn', label: 'Withdraw approval' },
          ]}
        />
        <Input placeholder="Your role (e.g. Oncology SME)" maxLength={80} onChange={(e) => { role = e.target.value; }} />
        <Input.TextArea rows={3} maxLength={4000} placeholder="What you checked, and against which source" onChange={(e) => { justification = e.target.value; }} />
      </div>
    ),
    onOk: async () => {
      try {
        await decideScenario(agentId, {
          scenarioId: row.scenarioId,
          scenarioSnapshotId: row.currentSnapshotId ?? 0,
          decision,
          reviewerRole: role.trim() || undefined,
          justification: justification.trim(),
        });
        void message.success('Recorded.');
        onDone();
      } catch (err) {
        void message.error((err as Error).message);
        throw err; // keep the dialog open
      }
    },
  });
}

const STANDING: Record<ScenarioStanding, { label: string; tone: PillTone }> = {
  Failed: { label: 'Fails now', tone: 'red' },
  Inconclusive: { label: 'Inconclusive', tone: 'slate' },
  NotTested: { label: 'Not tested on this version', tone: 'amber' },
  Passed: { label: 'Passes now', tone: 'green' },
  Quarantined: { label: 'Quarantined', tone: 'slate' },
  Retired: { label: 'Retired, still required', tone: 'amber' },
};

export const AgentEvaluationTab = observer(function AgentEvaluationTab({ agentId }: { agentId: number }) {
  const { themeStore } = useStore();
  const isDark = themeStore.mode === 'dark';
  const [overview, setOverview] = useState<AgentEvaluationOverview | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [openResultId, setOpenResultId] = useState<number | null>(null);
  const [version, setVersion] = useState(0);

  const load = useCallback(
    (signal?: AbortSignal) => {
      setLoading(true);
      getAgentEvaluationOverview(agentId, signal)
        .then((o) => {
          if (signal?.aborted) return;
          setOverview(o);
          setError(null);
        })
        .catch((err: Error) => !signal?.aborted && setError(err.message))
        .finally(() => !signal?.aborted && setLoading(false));
    },
    [agentId],
  );

  useEffect(() => {
    const controller = new AbortController();
    setOverview(null);
    load(controller.signal);
    return () => controller.abort();
  }, [load]);

  const divider = isDark ? colors.slate[700] : colors.slate[200];
  const headline = overview ? HEADLINE[overview.headline] : null;
  const count = (s: ScenarioStanding) => overview?.scenarios.filter((r) => r.standing === s).length ?? 0;
  const required = overview?.scenarios.filter((r) => r.required) ?? [];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16, paddingTop: 16 }}>
      {error && <Alert type="error" showIcon title="Could not load the evaluation" description={error} />}
      {!overview && !error && (
        <SectionCard>
          <Skeleton active paragraph={{ rows: 4 }} />
        </SectionCard>
      )}

      {overview && headline && (
        <SectionCard
          title={
            <Space size={8} wrap>
              <StatusPill tone={headline.tone} icon={headline.icon}>{headline.title}</StatusPill>
              <Text type="secondary" style={{ fontSize: 12, fontWeight: 400 }}>
                {`Required: ${required.filter((r) => r.standing === 'Passed').length} of ${required.length} passed on this version`}
              </Text>
            </Space>
          }
          subtitle="Judged on the agent as it is now: a result counts only if it tested this version of the agent, on the scenario as it is now, in the last 30 days. Eligible for release review is not an approval."
          actions={
            <Button size="small" icon={<ReloadOutlined />} loading={loading} onClick={() => { load(); setVersion((v) => v + 1); }}>
              Refresh
            </Button>
          }
        >
          <ul style={{ margin: '0 0 12px', paddingLeft: 20 }}>
            {overview.reasons.map((r) => (
              <li key={r}>
                <Text>{r}</Text>
              </li>
            ))}
          </ul>
          <Space size={[8, 8]} wrap style={{ marginBottom: 8 }}>
            <StatusPill tone="red">{`Fail now: ${count('Failed')}`}</StatusPill>
            <StatusPill tone="amber">{`Not tested on this version: ${count('NotTested')}`}</StatusPill>
            <StatusPill tone="slate">{`Inconclusive: ${count('Inconclusive')}`}</StatusPill>
            <StatusPill tone="green">{`Pass now: ${count('Passed')}`}</StatusPill>
            {count('Quarantined') > 0 && <StatusPill tone="slate">{`Quarantined: ${count('Quarantined')}`}</StatusPill>}
          </Space>
          <div>
            <Text type="secondary" style={{ fontSize: 12 }}>
              {overview.latestRunId != null ? (
                <>
                  {'Latest run '}
                  <Link to={`/runs/${overview.latestRunId}`}>{`#${overview.latestRunId}`}</Link>
                  {overview.latestRunUtc ? `, ${dayjs(overview.latestRunUtc).fromNow()}` : ''}
                </>
              ) : (
                'No run yet'
              )}
              {overview.unfinishedRequests > 0 ? ` · ${overview.unfinishedRequests} run request(s) in progress` : ''}
              {` · policy ${overview.policyVersion}`}
            </Text>
          </div>
        </SectionCard>
      )}

      {overview && overview.untestedChanges.length > 0 && (
        <Alert
          type="warning"
          showIcon
          title="Changed since the latest run, so not tested yet"
          description={
            <ul style={{ margin: 0, paddingLeft: 18 }}>
              {overview.untestedChanges.map((c) => (
                <li key={c}>{c}</li>
              ))}
            </ul>
          }
        />
      )}

      {overview && (
        <SectionCard
          title={`Scenarios (${overview.scenarios.length})`}
          subtitle="Where each active scenario stands on the agent as it is now. Safety scenarios are required. Open one to see its conversation."
        >
          {overview.scenarios.length === 0 ? (
            <Text type="secondary">This agent has no active scenarios.</Text>
          ) : (
            overview.scenarios.map((row, i) => {
              const s = STANDING[row.standing];
              const resultId = row.resultId ?? row.latestAnyVersionResultId ?? null;
              return (
                <div
                  key={row.scenarioId}
                  style={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    alignItems: 'center',
                    gap: '6px 12px',
                    padding: '8px 0',
                    borderTop: i === 0 ? undefined : `1px solid ${divider}`,
                  }}
                >
                  <div style={{ flex: '1 1 260px', minWidth: 0 }}>
                    <Space size={6} wrap>
                      <StatusPill tone={s.tone}>{s.label}</StatusPill>
                      {row.isSafety && <StatusPill tone="violet">Safety · required</StatusPill>}
                      {row.required && (
                        <StatusPill tone={row.expectationApproved ? 'green' : 'amber'}>
                          {row.expectationApproved ? 'Expectations approved' : 'Expectations not approved'}
                        </StatusPill>
                      )}
                      {row.finding && <StatusPill tone={FINDING[row.finding.disposition].tone}>{FINDING[row.finding.disposition].label}</StatusPill>}
                      <Text style={{ fontFamily: "'JetBrains Mono', monospace", wordBreak: 'break-word' }}>{row.scenarioId}</Text>
                    </Space>
                    <Text type="secondary" style={{ display: 'block', fontSize: 12 }}>
                      {row.reason}
                      {row.standing === 'NotTested' && row.latestAnyVersionRunUtc
                        ? ` Last result (${row.latestAnyVersionPassed ? 'passed' : 'failed'}) ${dayjs(row.latestAnyVersionRunUtc).fromNow()}.`
                        : ''}
                    </Text>
                  </div>
                  <Space size={4} wrap>
                    {row.required && row.currentSnapshotId != null && (
                      <Button size="small" onClick={() => reviewExpectations(agentId, row, () => { load(); setVersion((v) => v + 1); })}>
                        Review expectations…
                      </Button>
                    )}
                    {resultId != null && (
                      <Button size="small" onClick={() => setOpenResultId(resultId)}>
                        {row.resultId != null ? 'Open result' : 'Open last result'}
                      </Button>
                    )}
                  </Space>
                </div>
              );
            })
          )}
        </SectionCard>
      )}

      <ReleasePanel agentId={agentId} refreshToken={version} />
      <RunRequestsPanel agentId={agentId} refreshToken={version} />
      <FindingDrawer resultId={openResultId} onClose={() => { setOpenResultId(null); load(); }} />
    </div>
  );
});
